import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client/react';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Configure SDK client for cookie-based auth BEFORE any components mount
import './services/api/familypal/config';

import { apolloClient } from './graphql/client';
import { ThemeContextProvider, AuthProvider, FamilyProvider, SubscriptionProvider, useSubscription, HotkeysProvider, useAuth } from './context';
import { AppShell } from './components/layout';
import { LoadingSpinner, HotkeysIntegration } from './components/common';

// Auth pages
import { LoginPage, SignupPage, RecoveryPage } from './pages/auth';
// Main pages
import { HomePage } from './pages/home';
// Placeholder pages for routes
import CalendarPage from './pages/calendar/CalendarPage';
import MessagesPage from './pages/messages/MessagesPage';
import SchoolPage from './pages/school/SchoolPage';
import HealthPage from './pages/health/HealthPage';
import ActivitiesPage from './pages/activities/ActivitiesPage';
import ContactsPage from './pages/contacts/ContactsPage';
import MoneyPage from './pages/money/MoneyPage';
import DisciplinePage from './pages/discipline/DisciplinePage';
import NotebookPage from './pages/tasks/TasksPage';
import MemoriesPage from './pages/memories/MemoriesPage';
import SettingsPage from './pages/settings/SettingsPage';
import CustodyPage from './pages/custody/CustodyPage';
import DocumentsPage from './pages/documents/DocumentsPage';
import MailPage from './pages/mail/MailPage';
import InvitePage from './pages/invite/InvitePage';
import JoinPage from './pages/join/JoinPage';
import PaywallPage from './pages/paywall/PaywallPage';
// Protected route wrapper
const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullPage message="Loading..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <FamilyProvider>
      <SubscriptionProvider>
        <Outlet />
      </SubscriptionProvider>
    </FamilyProvider>
  );
};

// Requires active subscription — redirects to /paywall if not subscribed
const SubscribedRoute: React.FC = () => {
  const { isSubscribed, isLoading } = useSubscription();

  if (isLoading) {
    return <LoadingSpinner fullPage message="Loading..." />;
  }

  if (!isSubscribed) {
    return <Navigate to="/paywall" replace />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
};

// Public route wrapper (redirects to home if authenticated)
const PublicRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullPage message="Loading..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <ThemeContextProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            autoHideDuration={4000}
          >
            <BrowserRouter>
              <AuthProvider>
                <HotkeysProvider>
                  <HotkeysIntegration />
                  <Routes>
                    {/* Standalone public routes (no auth redirect) */}
                    <Route path="/invite/:code" element={<InvitePage />} />
                    <Route path="/join/:token" element={<JoinPage />} />

                    {/* Public routes */}
                    <Route element={<PublicRoute />}>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/recovery" element={<RecoveryPage />} />
                    </Route>

                    {/* Protected routes (auth required, subscription checked) */}
                    <Route element={<ProtectedRoute />}>
                      {/* Paywall and settings are accessible without subscription */}
                      <Route path="/paywall" element={<PaywallPage />} />
                      <Route element={<SubscribedRoute />}>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/calendar/*" element={<CalendarPage />} />
                        <Route path="/messages/*" element={<MessagesPage />} />
                        <Route path="/school/*" element={<SchoolPage />} />
                        <Route path="/health/*" element={<HealthPage />} />
                        <Route path="/activities/*" element={<ActivitiesPage />} />
                        <Route path="/contacts/*" element={<ContactsPage />} />
                        <Route path="/money/*" element={<MoneyPage />} />
                        <Route path="/discipline/*" element={<DisciplinePage />} />
                        <Route path="/notebook/*" element={<NotebookPage />} />
                        <Route path="/memories/*" element={<MemoriesPage />} />
                        <Route path="/documents/*" element={<DocumentsPage />} />
                        <Route path="/mail/*" element={<MailPage />} />
                        <Route path="/custody/*" element={<CustodyPage />} />
                        <Route path="/settings/*" element={<SettingsPage />} />
                      </Route>
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </HotkeysProvider>
              </AuthProvider>
            </BrowserRouter>
          </SnackbarProvider>
        </LocalizationProvider>
      </ThemeContextProvider>
    </ApolloProvider>
  );
}

export default App;
